import BaseInstance from './AxiosInstance/BaseInstance';
import { WORKFLOW, WORKFLOW_NODES } from './Endpoint';

export interface WorkflowNode {
  id: string;
  type: string;
  position: {
    x: number;
    y: number;
  };
  data: any;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  data?: {
    connectionType?: string;
    inputSlot?: string;
    aggregationType?: string;
    aggregationConfig?: any;
  };
}

export interface WorkflowVariable {
  id: string;
  variable_name: string;
  variable_value: string;
  variable_type: string;
  is_secret: boolean;
}

export interface SaveWorkflowRequest {
  name: string;
  description?: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  variables?: WorkflowVariable[];
}

export interface LoadWorkflowResponse {
  id: string;
  name: string;
  description?: string;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  variables: WorkflowVariable[];
  created_at: string;
  updated_at?: string;
}

export interface WorkflowListItem {
  id: string;
  name: string;
  description?: string;
  status: string;
  is_public: boolean;
  is_workflow?: boolean;  // Flag to identify this as a workflow for embed differentiation
  created_at: string;
  updated_at?: string;
  created_by?: string;
}

class WorkflowService {
  // Get all workflows with pagination
  async getWorkflows(page: number = 1, pageSize: number = 8, search?: string): Promise<{ items: WorkflowListItem[], pagination: { total: number, page: number, page_size: number } }> {
    try {
      // Use classic workflows listing endpoint for the list page with pagination
      let url = `${WORKFLOW.GET_WORKFLOWS}?page=${page}&page_size=${pageSize}`;
      if (search) {
        url += `&search=${search}`;
      }
      const response = await BaseInstance.get(url);
      // Inject is_workflow flag for all workflow items
      const data = response.data;
      if (data.items) {
        data.items = data.items.map((item: WorkflowListItem) => ({ ...item, is_workflow: true }));
      }
      return data;
    } catch (error) {
      console.error('Error fetching workflows:', error);
      throw error;
    }
  }

  // Save a new workflow
  async saveWorkflow(workflowData: SaveWorkflowRequest): Promise<WorkflowListItem> {
    try {
      const response = await BaseInstance.post(WORKFLOW_NODES.SAVE_WORKFLOW, workflowData);
      return { ...response.data, is_workflow: true };
    } catch (error) {
      console.error('Error saving workflow:', error);
      throw error;
    }
  }

  // Load a specific workflow
  async loadWorkflow(workflowId: string): Promise<LoadWorkflowResponse> {
    try {
      const response = await BaseInstance.get(`${WORKFLOW_NODES.GET_WORKFLOW}/${workflowId}`);
      return response.data;
    } catch (error) {
      console.error('Error loading workflow:', error);
      throw error;
    }
  }

  // Update workflow data (nodes, edges, etc.)
  async updateWorkflowData(workflowId: string, workflowData: SaveWorkflowRequest): Promise<WorkflowListItem> {
    try {
      const response = await BaseInstance.put(`${WORKFLOW_NODES.UPDATE_WORKFLOW_DATA}/${workflowId}/save`, workflowData);
      return { ...response.data, is_workflow: true };
    } catch (error) {
      console.error('Error updating workflow data:', error);
      throw error;
    }
  }

  // Update workflow basic info (name, description, status)
  async updateWorkflow(workflowId: string, updateData: {
    name?: string;
    description?: string;
    status?: string;
    is_public?: boolean;
  }): Promise<WorkflowListItem> {
    try {
      const response = await BaseInstance.put(`${WORKFLOW_NODES.UPDATE_WORKFLOW}/${workflowId}`, updateData);
      return { ...response.data, is_workflow: true };
    } catch (error) {
      console.error('Error updating workflow:', error);
      throw error;
    }
  }

  // Delete a workflow
  async deleteWorkflow(workflowId: string): Promise<WorkflowListItem> {
    try {
      const response = await BaseInstance.delete(`${WORKFLOW_NODES.DELETE_WORKFLOW}/${workflowId}`);
      return { ...response.data, is_workflow: true };
    } catch (error) {
      console.error('Error deleting workflow:', error);
      throw error;
    }
  }

  // Convert React Flow format to API format
  convertToApiFormat(nodes: any[], edges: any[]): { nodes: WorkflowNode[], edges: any[] } {
    const nodeIdToType: Record<string, string> = {};
    const nodeIdToData: Record<string, any> = {};
    nodes.forEach((n) => {
      nodeIdToType[n.id] = n.type;
      nodeIdToData[n.id] = n.data ?? {};
    });
    console.log('convertToApiFormat - Input Nodes:', nodes.length, 'Edges:', edges.length);

    // Build supervisor->agents mapping from edges
    const supervisorIdToAgents: Record<string, string[]> = {};
    const isAgentsEdge = (e: any) => {
      if (e.sourceHandle === 'agents') return true;
      // Fallback heuristic if handles are missing
      return nodeIdToType[e.source] === 'supervisor' && nodeIdToType[e.target] === 'agent';
    };

    // Build conditionalRoute->falseConnections mapping from edges
    const conditionalRouteIdToFalseConnections: Record<string, string[]> = {};
    const isFalseEdge = (e: any) => {
      if (e.sourceHandle === 'false') return true;
      // Fallback heuristic if handles are missing
      return nodeIdToType[e.source] === 'conditionalRoute' && e.sourceHandle === 'false';
    };

    const filteredEdges: any[] = [];
    edges.forEach((e) => {
      const edgeOut = {
        id: e.id,
        source: e.source,
        target: e.target,
        // ensure supervisor -> output/conditionalRoute uses the 'output' handle
        sourceHandle: (e.sourceHandle || (nodeIdToType[e.source] === 'supervisor' && (nodeIdToType[e.target] === 'output' || nodeIdToType[e.target] === 'conditionalRoute') ? 'output' : undefined)),
        targetHandle: e.targetHandle,
        data: e.data || {},
      };
      if (isAgentsEdge(e)) {
        console.log('convertToApiFormat - Found Agent Edge:', e.source, '->', e.target);
        const list = supervisorIdToAgents[e.source] || [];
        list.push(e.target);
        supervisorIdToAgents[e.source] = list;
        // Also include this edge in persisted edges so the backend knows about the graph connection
        // This is critical if the backend regenerates IDs but doesn't update the 'agents' data blob
        filteredEdges.push(edgeOut);
        return;
      }

      if (isFalseEdge(e)) {
        const list = conditionalRouteIdToFalseConnections[e.source] || [];
        list.push(e.target);
        conditionalRouteIdToFalseConnections[e.source] = list;
        // Do not include this edge in persisted edges
        return;
      }

      filteredEdges.push(edgeOut);
    });

    const apiNodes: WorkflowNode[] = nodes.map((node) => {
      const data = { ...(node.data ?? {}) } as any;
      if (node.type === 'supervisor') {
        const agents = supervisorIdToAgents[node.id];
        if (agents && agents.length) {
          data.agents = agents;
        } else if ('agents' in data && (!Array.isArray(data.agents) || data.agents.length === 0)) {
          // Clean empty agents arrays
          delete data.agents;
        }
      }
      if (node.type === 'conditionalRoute') {
        const falseConnections = conditionalRouteIdToFalseConnections[node.id];
        if (falseConnections && falseConnections.length) {
          data.falseConnections = falseConnections;
        } else if ('falseConnections' in data && (!Array.isArray(data.falseConnections) || data.falseConnections.length === 0)) {
          // Clean empty falseConnections arrays
          delete data.falseConnections;
        }
      }
      return {
        id: node.id,
        type: node.type,
        position: { x: node.position.x, y: node.position.y },
        data,
      };
    });

    console.log('convertToApiFormat - Output Nodes:', apiNodes);
    return { nodes: apiNodes, edges: filteredEdges };
  }

  // Convert API format to React Flow format
  convertToReactFlowFormat(apiNodes: WorkflowNode[], apiEdges: any[]): { nodes: any[], edges: any[] } {
    const reactFlowNodes = apiNodes.map((node) => ({
      id: node.id,
      type: node.type,
      position: node.position,
      data: node.data,
    }));
    console.log('convertToReactFlowFormat - Input Nodes:', apiNodes);

    const reactFlowEdges: any[] = apiEdges.map((edge: any) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      type: 'animatedDashed',
      animated: true,
      style: { stroke: '#666' },
      data: edge.data || {},
    }));

    // Reconstruct visual supervisor->agent edges from node.data (DB field node_data) for editing UI
    const nodeById: Record<string, any> = Object.fromEntries(reactFlowNodes.map((n) => [n.id, n]));
    reactFlowNodes.forEach((n) => {
      const agentsList = (n.data && n.data.agents) as string[] | undefined;
      if (n.type === 'supervisor' && Array.isArray(agentsList)) {
        agentsList.forEach((agentId: string) => {
          // Verify the agent node exists before creating a connection
          if (nodeById[agentId]) {
            reactFlowEdges.push({
              id: `agent-link-${n.id}-${agentId}`,
              source: n.id,
              target: agentId,
              sourceHandle: 'agents',
              targetHandle: null, // Explicitly null for custom edges if needed, or undefined
              type: 'animatedDashed',
              animated: true,
              style: { stroke: '#666' },
              data: { connectionType: 'agents' },
            });
          } else {
            console.warn(`Supervisor ${n.id} references missing agent ${agentId}`);
          }
        });
      }

      // Reconstruct visual false connections for conditional route nodes
      const falseConnectionsList = (n.data && n.data.falseConnections) as string[] | undefined;
      if (n.type === 'conditionalRoute' && Array.isArray(falseConnectionsList)) {
        falseConnectionsList.forEach((targetId: string) => {
          if (nodeById[targetId]) {
            reactFlowEdges.push({
              id: `false-link-${n.id}-${targetId}`,
              source: n.id,
              target: targetId,
              sourceHandle: 'false',
              targetHandle: undefined,
              type: 'animatedDashed',
              animated: true,
              style: { stroke: '#666' },
              data: { connectionType: 'false' },
            });
          }
        });
      }
    });

    // Ensure supervisor -> output/conditionalRoute uses the 'output' handle in editor view too
    reactFlowEdges.forEach((e) => {
      const sourceNode = nodeById[e.source];
      const targetNode = nodeById[e.target];
      const isSupervisorToOutput = sourceNode?.type === 'supervisor' && targetNode?.type === 'output';
      const isSupervisorToConditionalRoute = sourceNode?.type === 'supervisor' && targetNode?.type === 'conditionalRoute';

      if (isSupervisorToOutput || isSupervisorToConditionalRoute) {
        e.sourceHandle = 'output';
      }
    });

    return { nodes: reactFlowNodes, edges: reactFlowEdges };
  }
}

export default new WorkflowService();
